import { useCallback, useState } from "react";
import type { StorageDriver, StorageKey } from "@exonn/storage";
import { memoryStorageDriver } from "src/storage/memoryStorage.driver.ts";

export const useStorageState = <S>(
  storageKey: StorageKey,
  defaultValue: S | { (): S },
  driver: StorageDriver = memoryStorageDriver,
  options?: {
    disablePersistence?: boolean;
  },
) => {
  const { disablePersistence = false } = options || {};
  const storageValue = !disablePersistence
    ? driver.get<S>(storageKey)
    : undefined;
  // todo: sync this state across tabs
  const [value, setValueInternal] = useState<S>(storageValue ?? defaultValue);

  const setValue = useCallback(
    (state: S | ((state: S) => S)) => {
      const cb = (prevState: S) => {
        const res = isUpdater<S>(state) ? state(prevState) : state;
        if (!disablePersistence) {
          driver.set<S>(storageKey, res);
        }
        return res;
      };
      setValueInternal(cb);
    },
    [storageKey, driver, disablePersistence],
  );

  const resetValue = useCallback(() => {
    if (!disablePersistence) {
      driver.remove(storageKey);
    }

    setValueInternal(defaultValue);
  }, [storageKey, driver, defaultValue, disablePersistence]);

  const getValue = useCallback(() => {
    return driver.get<S>(storageKey);
  }, [driver, storageKey]);

  const clearStorage = () => {
    driver.remove(storageKey);
  };

  return [
    value,
    setValue,
    {
      resetValue,
      getValue,
      clearStorage,
    },
  ] as const;
};

function isUpdater<S>(value: unknown): value is (prev: S) => S {
  return typeof value === "function";
}
